import { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, X } from "lucide-react";
import ConsolidatedRailCard from "@/components/ConsolidatedRailCard";
import { format, subDays, startOfMonth, endOfMonth, startOfYear } from "date-fns";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  LineChart, Line,
} from "recharts";

const COLORS = ["#E07A5F", "#2F4F4F", "#4C956C", "#E63946", "#D4A373", "#8AA9A9", "#B08968"];
const PRESETS = [
  { key: "30d", label: "Last 30 days", fn: () => ({ start: subDays(new Date(), 30), end: new Date() }) },
  { key: "month", label: "This month", fn: () => ({ start: startOfMonth(new Date()), end: endOfMonth(new Date()) }) },
  { key: "ytd", label: "Year to date", fn: () => ({ start: startOfYear(new Date()), end: new Date() }) },
  { key: "all", label: "All time", fn: () => ({ start: null, end: null }) },
];

export default function Analytics() {
  const { user } = useAuth();
  const primaryCurrency = user?.currency || "USD";
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ start: null, end: null, preset: "all" });
  const { activeRail, setActiveRail } = useCurrencyRail();

  const load = useCallback(async () => {
    const params = {};
    if (range.start) params.start = format(range.start, "yyyy-MM-dd");
    if (range.end) params.end = format(range.end, "yyyy-MM-dd");
    const { data } = await api.get("/analytics/summary", { params });
    setData(data);
  }, [range.start, range.end]);

  useEffect(() => { load(); }, [load]);

  const rails = Array.isArray(data?.by_currency) ? data.by_currency : [];
  const multi = rails.length > 1;
  // Which rail's data drives the charts. Uses global sidebar rail if set; falls back to primary.
  const railKey = activeRail !== "ALL" ? activeRail : primaryCurrency;
  const currentRail = multi
    ? rails.find((r) => r.currency === railKey) || rails[0]
    : (rails.length === 1 ? rails[0] : null);
  const currency = currentRail?.currency || primaryCurrency;
  const catExpense = useMemo(
    () => currentRail ? currentRail.by_category_expense : (data?.by_category_expense || []),
    [currentRail, data]
  );
  const monthlyTrend = useMemo(
    () => currentRail ? currentRail.monthly_trend : (data?.monthly_trend || []),
    [currentRail, data]
  );

  const pieData = useMemo(() => (catExpense || []).map((d) => ({
    name: d.category, value: d.amount,
  })), [catExpense]);

  const barData = useMemo(() => {
    return (monthlyTrend || []).map((m) => ({
      month: m.month, income: m.income, expense: m.expense, saving: m.saving,
    }));
  }, [monthlyTrend]);

  const applyPreset = (key) => {
    const p = PRESETS.find((x) => x.key === key);
    const r = p.fn();
    setRange({ ...r, preset: key });
  };

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Insights</div>
          <h1 className="font-display text-4xl mt-2">Analytics</h1>
        </div>
      </div>

      <div data-testid="analytics-filters" className="card-flat p-4 mb-4 flex flex-wrap gap-2 items-center">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)] mr-2">Range</div>
        {PRESETS.map((p) => (
          <Button
            key={p.key}
            data-testid={`preset-${p.key}`}
            variant={range.preset === p.key ? "default" : "outline"}
            size="sm"
            onClick={() => applyPreset(p.key)}
            className={`rounded-full ${range.preset === p.key ? "bg-[#2F4F4F] text-[#FDFCF8]" : ""}`}
          >
            {p.label}
          </Button>
        ))}
        <div className="mx-1 h-6 w-px bg-[var(--border)]" />
        <Popover>
          <PopoverTrigger asChild>
            <Button data-testid="range-start" variant="outline" size="sm" className="rounded-full">
              <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
              {range.start ? format(range.start, "MMM d, yyyy") : "Start date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar mode="single" selected={range.start} onSelect={(d) => setRange({ ...range, start: d, preset: "custom" })} initialFocus />
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button data-testid="range-end" variant="outline" size="sm" className="rounded-full">
              <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
              {range.end ? format(range.end, "MMM d, yyyy") : "End date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar mode="single" selected={range.end} onSelect={(d) => setRange({ ...range, end: d, preset: "custom" })} initialFocus />
          </PopoverContent>
        </Popover>
        {(range.start || range.end) && (
          <Button data-testid="range-clear" variant="ghost" size="sm" onClick={() => setRange({ start: null, end: null, preset: "all" })}>
            <X className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        )}
      </div>

      {multi && (
        <>
          <div className="mb-4">
            <ConsolidatedRailCard
              rails={rails}
              testId="analytics-consolidated"
              subtitle="income + expenses + balance across every rail"
              fields={[
                { key: "income", label: "Income", color: "text-[#2F6B4A]" },
                { key: "expense", label: "Expenses", color: "text-[#B4283A]" },
                { key: "balance", label: "Net balance",
                  color: rails.reduce((a, r) => a + (r.balance || 0), 0) >= 0 ? "text-[#4C956C]" : "text-[#E63946]" },
              ]}
            />
          </div>
          <div data-testid="analytics-rail-picker" className="card-flat p-3 mb-4 flex flex-wrap gap-2 items-center">
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)] mr-2">Currency rail</div>
          {rails.map((r) => (
            <Button
              key={r.currency}
              data-testid={`rail-${r.currency}`}
              variant={currency === r.currency ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveRail(r.currency)}
              className={`rounded-full ${currency === r.currency ? "bg-[#2F4F4F] text-[#FDFCF8]" : ""}`}
              style={currency === r.currency ? { backgroundColor: r.currency === "USD" ? "#1E5A96" : "#4C956C" } : undefined}
            >
              {r.currency} · balance {formatCurrency(r.balance, r.currency)}
            </Button>
          ))}
          <span className="text-[10px] text-[var(--muted-text)] ml-1">
            Charts below reflect only the selected rail — no cross-currency summing.
          </span>
        </div>
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-flat p-6">
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Category breakdown</div>
          <h3 className="font-display text-xl mt-1">Where your money goes</h3>
          <div className="h-72 mt-4" data-testid="pie-chart">
            {pieData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-[var(--muted-text)]">
                Add expenses to see breakdown.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value">
                    {pieData.map((entry, i) => (
                      <Cell key={entry.name} fill={COLORS[i % COLORS.length]} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v) => formatCurrency(v, currency)}
                    contentStyle={{ border: "1px solid var(--border)", borderRadius: 8, background: "var(--surface)", color: "var(--text)" }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="card-flat p-6">
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Monthly comparison</div>
          <h3 className="font-display text-xl mt-1">Income vs expense</h3>
          <div className="h-72 mt-4" data-testid="bar-chart">
            {barData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-[var(--muted-text)]">
                Add transactions to see comparison.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="2 4" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--muted-text)" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "var(--muted-text)" }} axisLine={false} tickLine={false} width={45} />
                  <Tooltip
                    formatter={(v) => formatCurrency(v, currency)}
                    contentStyle={{ border: "1px solid var(--border)", borderRadius: 8, background: "var(--surface)", color: "var(--text)" }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="income" fill="#4C956C" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expense" fill="#E63946" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="saving" fill="#E07A5F" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 card-flat p-6">
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Trend</div>
          <h3 className="font-display text-xl mt-1">Cash flow over time</h3>
          <div className="h-72 mt-4" data-testid="line-chart">
            {barData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-[var(--muted-text)]">
                No data yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={barData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="2 4" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--muted-text)" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "var(--muted-text)" }} axisLine={false} tickLine={false} width={45} />
                  <Tooltip
                    formatter={(v) => formatCurrency(v, currency)}
                    contentStyle={{ border: "1px solid var(--border)", borderRadius: 8, background: "var(--surface)", color: "var(--text)" }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="income" stroke="#4C956C" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="expense" stroke="#E63946" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="saving" stroke="#E07A5F" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
